import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../components/ui/tooltip";
import { toast, Toaster } from "sonner";
import { Ticket, Send, CheckCircle, RefreshCw, Sun, Moon, Plus, X, Info } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Category definitions with descriptions
const TICKET_CATEGORIES = [
  {
    value: "Access & Identity",
    label: "Access & Identity",
    description: "New user setup / termination, Role or permission changes, Password resets, MFA issues, OEM or vendor portal access"
  },
  {
    value: "Hardware & Devices",
    label: "Hardware & Devices",
    description: "Laptops, desktops, tablets, Printers and scanners, Mobile phones, Peripheral issues, Device replacements"
  },
  {
    value: "Software & Applications",
    label: "Software & Applications",
    description: "DMS / CRM / F&I system issues, Software installs or updates, License requests, Application configuration questions, App performance problems"
  },
  {
    value: "Connectivity & Network",
    label: "Connectivity & Network",
    description: "Internet outages, Wi-Fi issues, VPN access, Network performance, Email connectivity"
  },
  {
    value: "Data, Reports & Integrations",
    label: "Data, Reports & Integrations",
    description: "Report discrepancies, Data corrections, Integration failures, Sync issues, Export/import requests"
  },
  {
    value: "Security & Compliance",
    label: "Security & Compliance",
    description: "Phishing, Security incidents"
  },
  {
    value: "How-To & Training",
    label: "How-To & Training",
    description: "General how-to questions, Training requests, Documentation needs"
  }
];

export default function PublicTicketForm() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    title: "",
    description: "",
    priority: "medium",
    computer_name: "",
    category: "",
    dealership: "",
    department: "",
    additional_emails: []
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [ticketNumber, setTicketNumber] = useState("");
  const [dealerships, setDealerships] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [theme, setTheme] = useState(() => {
    return localStorage.getItem("theme") || "light";
  });

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    localStorage.setItem("theme", theme);
  }, [theme]);

  useEffect(() => {
    fetchDealershipsAndDepartments();
  }, []);

  const fetchDealershipsAndDepartments = async () => {
    try {
      const [dealershipRes, departmentRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/public/dealerships`),
        axios.get(`${BACKEND_URL}/api/public/departments`)
      ]);
      setDealerships(dealershipRes.data);
      setDepartments(departmentRes.data);
    } catch (error) {
      console.error("Failed to fetch dealerships/departments", error);
    }
  };

  const toggleTheme = () => {
    setTheme(prev => prev === "light" ? "dark" : "light");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation - All fields mandatory (matching CreateTicketModal)
    if (!formData.name.trim()) {
      toast.error("Please enter your name");
      return;
    }
    if (!formData.email.trim()) {
      toast.error("Please enter your email");
      return;
    }
    if (!formData.title.trim()) {
      toast.error("Please enter a summary");
      return;
    }
    if (!formData.description.trim()) {
      toast.error("Please enter a description");
      return;
    }
    if (!formData.priority) {
      toast.error("Please select a priority");
      return;
    }
    if (!formData.computer_name.trim()) {
      toast.error("Please enter device name");
      return;
    }
    if (!formData.category) {
      toast.error("Please select a category");
      return;
    }
    if (!formData.dealership) {
      toast.error("Please select a dealership");
      return;
    }
    if (!formData.department) {
      toast.error("Please select a department");
      return;
    }

    try {
      setSubmitting(true);
      const response = await axios.post(`${BACKEND_URL}/api/public/submit-ticket`, formData);
      
      setTicketNumber(response.data.ticket_number);
      setSubmitted(true);
      toast.success("Your support request has been submitted!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const addEmailField = () => {
    setFormData(prev => ({
      ...prev,
      additional_emails: [...prev.additional_emails, ""]
    }));
  };

  const updateEmailField = (index, value) => {
    setFormData(prev => {
      const newEmails = [...prev.additional_emails];
      newEmails[index] = value;
      return { ...prev, additional_emails: newEmails };
    });
  };

  const removeEmailField = (index) => {
    setFormData(prev => ({
      ...prev,
      additional_emails: prev.additional_emails.filter((_, i) => i !== index)
    }));
  };

  const handleNewTicket = () => {
    setFormData({
      name: "",
      email: "",
      phone: "",
      title: "",
      description: "",
      priority: "medium",
      computer_name: "",
      category: "",
      dealership: "",
      department: "",
      additional_emails: []
    });
    setSubmitted(false);
    setTicketNumber("");
  };

  // Success screen after submission
  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 dark:from-[#1E1E1E] dark:to-[#252525] flex items-center justify-center p-4">
        <Toaster position="top-right" richColors />
        <Card className="w-full max-w-lg shadow-[0_20px_80px_-10px_rgba(0,0,0,0.35)] border-0">
          <CardContent className="pt-12 pb-8 px-8 text-center">
            <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600 dark:text-green-400" />
            </div>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-2">
              Request Submitted!
            </h2>
            <p className="text-slate-600 dark:text-slate-300 mb-6">
              Your support request has been received and our IT team will assist you shortly.
            </p>
            <div className="bg-slate-100 dark:bg-[#343434] rounded-lg p-4 mb-6">
              <p className="text-sm text-slate-500 dark:text-slate-400 mb-1">Your Ticket Number</p>
              <p className="text-2xl font-mono font-bold text-[#7a1d42]">{ticketNumber}</p>
            </div>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
              Please save this ticket number for your reference. You will also receive a confirmation email.
            </p>
            <Button 
              onClick={handleNewTicket}
              className="bg-[#7a1d42] hover:bg-[#5a1531] text-white"
            >
              Submit Another Request
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 dark:from-[#1E1E1E] dark:to-[#252525] py-8 px-4">
      <Toaster position="top-right" richColors />
      
      {/* Theme Toggle */}
      <div className="fixed top-4 right-4">
        <Button
          variant="outline"
          size="icon"
          onClick={toggleTheme}
          className="rounded-full"
        >
          {theme === "light" ? (
            <Moon className="w-4 h-4" />
          ) : (
            <Sun className="w-4 h-4" />
          )}
        </Button>
      </div>

      <div className="max-w-2xl mx-auto">
        {/* Header with Logo */}
        <div className="text-center mb-8">
          <div className="inline-block bg-white dark:bg-[#2C2C2C] rounded-xl p-6 shadow-[0_20px_80px_-10px_rgba(0,0,0,0.35)] mb-6">
            <img 
              src="/niello-logo.png" 
              alt="Niello" 
              className="h-16 mx-auto"
            />
          </div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-white mb-2">
            IT Support Request
          </h1>
          <p className="text-slate-600 dark:text-slate-300">
            Submit a new support ticket. Our IT team will respond shortly.
          </p>
        </div>

        {/* Form Card */}
        <Card className="shadow-[0_20px_80px_-10px_rgba(0,0,0,0.35)] border-0">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Ticket className="w-5 h-5 text-[#7a1d42]" />
              Create New Ticket
            </CardTitle>
            <CardDescription>
              All fields marked with * are required
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Contact Information - Public form only */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pb-4 border-b">
                <div className="space-y-2">
                  <Label htmlFor="name">Your Name *</Label>
                  <Input
                    id="name"
                    type="text"
                    placeholder="John Smith"
                    value={formData.name}
                    onChange={(e) => updateField("name", e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Your Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="jsmith@niello.com"
                    value={formData.email}
                    onChange={(e) => updateField("email", e.target.value)}
                    required
                  />
                </div>
              </div>

              {/* Title - Matching CreateTicketModal exactly */}
              <div className="space-y-2">
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  type="text"
                  placeholder="Brief description of the issue"
                  value={formData.title}
                  onChange={(e) => updateField("title", e.target.value)}
                  required
                  maxLength={50}
                />
                <p className="text-xs text-slate-500">{formData.title.length}/50 characters</p>
              </div>

              {/* Description - Matching CreateTicketModal exactly */}
              <div className="space-y-2">
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  placeholder="Provide detailed information about the issue..."
                  value={formData.description}
                  onChange={(e) => updateField("description", e.target.value)}
                  required
                  maxLength={500}
                  rows={5}
                />
                <p className="text-xs text-slate-500">{formData.description.length}/500 characters</p>
              </div>

              {/* Priority & Device Name - Matching CreateTicketModal exactly */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="priority">Priority *</Label>
                  <Select value={formData.priority} onValueChange={(value) => updateField("priority", value)} required>
                    <SelectTrigger>
                      <SelectValue placeholder="Select priority" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="critical">Critical</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="computer-name">Device Name *</Label>
                  <Input
                    id="computer-name"
                    type="text"
                    value={formData.computer_name}
                    onChange={(e) => updateField("computer_name", e.target.value)}
                    placeholder="Enter Device Name"
                    required
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => window.open('ms-settings:about', '_blank')}
                    className="text-xs"
                  >
                    Find Device Name
                  </Button>
                </div>

                {/* Category - Matching CreateTicketModal exactly */}
                <div className="space-y-2">
                  <Label htmlFor="category">Category *</Label>
                  <Select value={formData.category} onValueChange={(value) => updateField("category", value)} required>
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      <TooltipProvider delayDuration={200}>
                        {TICKET_CATEGORIES.map((cat) => (
                          <Tooltip key={cat.value}>
                            <TooltipTrigger asChild>
                              <SelectItem value={cat.value} className="cursor-pointer">
                                <span className="flex items-center gap-2">
                                  {cat.label}
                                  <Info className="w-3.5 h-3.5 text-slate-400" />
                                </span>
                              </SelectItem>
                            </TooltipTrigger>
                            <TooltipContent side="right" className="max-w-xs">
                              <p className="text-sm">{cat.description}</p>
                            </TooltipContent>
                          </Tooltip>
                        ))}
                      </TooltipProvider>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Dealership & Department - Matching CreateTicketModal exactly */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="dealership">Dealership *</Label>
                  <Select value={formData.dealership} onValueChange={(value) => updateField("dealership", value)} required>
                    <SelectTrigger>
                      <SelectValue placeholder="Select dealership" />
                    </SelectTrigger>
                    <SelectContent>
                      {dealerships.map((d) => (
                        <SelectItem key={d} value={d}>{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="department">Department *</Label>
                  <Select value={formData.department} onValueChange={(value) => updateField("department", value)} required>
                    <SelectTrigger>
                      <SelectValue placeholder="Select department" />
                    </SelectTrigger>
                    <SelectContent>
                      {departments.map((d) => (
                        <SelectItem key={d} value={d}>{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Additional Email Addresses - Matching CreateTicketModal exactly */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Additional Email Addresses (Optional)</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addEmailField}
                    className="flex items-center gap-1"
                  >
                    <Plus className="h-4 w-4" />
                    Add Email
                  </Button>
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Add email addresses that should receive notifications about this ticket.
                </p>
                {formData.additional_emails.map((email, index) => (
                  <div key={`email-field-${index}`} className="flex gap-2">
                    <Input
                      type="email"
                      placeholder="email@example.com"
                      value={email}
                      onChange={(e) => updateEmailField(index, e.target.value)}
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => removeEmailField(index)}
                      className="px-2"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>

              {/* Submit Button */}
              <div className="pt-4">
                <Button 
                  type="submit" 
                  className="w-full bg-[#7a1d42] hover:bg-[#5a1531] text-white h-12 text-base"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Create Ticket
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
